/**
 * Builds a lookup map from Slack emoji short names (e.g. "trophy") to their
 * unicode character representation (e.g. "🏆") using emoji-datasource.
 *
 * The raw JSON (~1.3 MB) is loaded via a dynamic import so it lands in a
 * separate async chunk and never bloats the main bundle. Loading kicks off
 * as soon as this module is evaluated, so the map is typically populated
 * before the user types their first :emoji: code. If the data hasn't arrived
 * yet, the transformer falls back to the raw :code: string (safe behaviour).
 *
 * Slack accepts native unicode emoji when publishing messages, so converting
 * colon-codes to unicode for editor display is safe — the round-tripped value
 * works identically with the Slack API.
 */

interface EmojiEntry {
  unified: string;
  short_name: string;
  short_names: string[];
}

function unifiedToChar(unified: string): string {
  return String.fromCodePoint(
    ...unified.split("-").map((hex) => parseInt(hex, 16)),
  );
}

// Exported as a plain object so callers hold a stable reference that gets
// populated in-place once the async chunk finishes loading.
const emojiMap: Record<string, string> = {};

// Fire-and-forget — no await needed at call sites.
import("emoji-datasource/emoji.json").then(({ default: emojiData }) => {
  for (const entry of emojiData as EmojiEntry[]) {
    const char = unifiedToChar(entry.unified);
    emojiMap[entry.short_name] = char;
    for (const alias of entry.short_names) {
      emojiMap[alias] = char;
    }
  }
});

export default emojiMap;
